import { ref, nextTick } from 'vue'
import { ComponentTypes } from '@canvas-builder/types/canvas'

const embeddableHeights = ref<Record<string, number>>({})

export default function useEmbeddable() {
  const queryWithRetry = async (selector: string, maxRetries: number = 2): Promise<NodeListOf<Element>> => {
    await nextTick()

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const elements = document.querySelectorAll(selector)

      if (elements.length > 0) {
        return elements
      }

      if (attempt < maxRetries) {
        const backoffMs = Math.min(1000 * Math.pow(2, attempt), 5000)
        await new Promise((resolve) => setTimeout(resolve, backoffMs))
      } else {
        console.warn(`No elements found for selector "${selector}" after ${maxRetries + 1} attempts`)
      }
    }

    return document.querySelectorAll(selector) // Return empty NodeList
  }

  const updateEmbeddableHeights = async () => {
    const embeddables = await queryWithRetry(`[data-component-id^="${ComponentTypes.Embeddable}"]`)

    for (const el of embeddables) {
      const componentId = el.getAttribute('data-component-id')
      if (componentId) {
        const height = el.getBoundingClientRect().height
        embeddableHeights.value[componentId] = height
      }
    }
  }

  const updateSectionEmbeddableHeights = async () => {
    const sectionEmbeddables = await queryWithRetry(`[data-section-component-id^="${ComponentTypes.Embeddable}"]`)

    for (const el of sectionEmbeddables) {
      const componentId = el.getAttribute('data-section-component-id')
      if (componentId) {
        const height = el.getBoundingClientRect().height
        embeddableHeights.value[componentId] = height
      }
    }
  }

  const getEmbeddableHeight = (componentId: string): number => {
    return embeddableHeights.value[componentId] ?? -1
  }

  const hasZeroHeight = (componentId: string): boolean => {
    return embeddableHeights.value[componentId] === 0
  }

  return {
    embeddableHeights,
    getEmbeddableHeight,
    hasZeroHeight,
    updateEmbeddableHeights,
    updateSectionEmbeddableHeights,
  }
}
